import { KgsBankSelect } from '../ui/kgs-bank-select';
import { kgsBankId } from '../../lib/kgsBanks';
import { MAX_DEPOSIT_ORDER_CAPACITY, DEPOSIT_ORDER_CAPACITY_ERROR, isValidDepositOrderCapacity } from '../../lib/orderCapacity';
import React, { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ordersAPI, traderAPI, OrderCreate } from '../../lib/api';
import { formatAmount } from '../../lib/utils';
import { balanceEvents, BALANCE_EVENTS } from '../../lib/events';
import { TRADER_BALANCE_REFRESH_INTERVAL_MS } from '../../lib/polling';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { BankAutocomplete } from '../ui/bank-autocomplete';
import { PhoneInput } from '../ui/phone-input';
import { CardInput } from '../ui/card-input';
import { ArrowRightLeft, Loader2, Plus } from 'lucide-react';

// Схема валидации
const orderSchema = z.object({
    from_currency: z.string(),
    to_currency: z.string(),
    from_amount: z.number().min(0.01, 'Введите сумму больше 0'),
    min_amount: z.number().optional(),
    max_amount: z.number().optional(),
    duration_minutes: z.number().min(1, 'Минимальное время жизни 1 минута').max(10080, 'Максимальное время жизни 7 дней (10080 минут)'),
    order_type: z.enum(['deposit', 'withdraw']),
    requisite_types: z.array(z.enum(['sbp', 'card', 'elqr'])).optional(),
    phone_number: z.string().optional().refine((val) => {
        if (!val) return true; // Поле опциональное
        const digits = val.replace(/\D/g, '');
        return (digits.length === 11 && digits.startsWith('7'))
            || (digits.length === 12 && digits.startsWith('996'));
    }, 'Введите корректный номер телефона'),
    recipient_bank: z.string().optional(),
    recipient_name: z.string().optional(),
    recipient_card: z.string().optional().refine((val) => {
        if (!val) return true; // Поле опциональное
        const digits = val.replace(/\D/g, '');
        return digits.length >= 13 && digits.length <= 19;
    }, 'Введите корректный номер карты'),
    qr_url: z.string().optional(),
    notes: z.string().optional(),
}).refine((data) => {
    // Валидация диапазона только для пополнения
    if (data.order_type === 'deposit') {
        if (!data.min_amount || !data.max_amount) {
            return false;
        }
        return data.max_amount >= data.min_amount;
    }
    return true;
}, {
    message: "Максимальная сумма должна быть больше или равна минимальной",
    path: ["max_amount"]
}).refine((data) => {
    // Валидация минимальной суммы только для пополнения
    if (data.order_type === 'deposit' && data.min_amount) {
        return data.min_amount <= data.from_amount;
    }
    return true;
}, {
    message: "Минимальная сумма не должна превышать общую сумму ордера",
    path: ["min_amount"]
}).refine((data) => {
    // Валидация максимальной суммы только для пополнения
    if (data.order_type === 'deposit' && data.max_amount) {
        return data.max_amount <= data.from_amount;
    }
    return true;
}, {
    message: "Максимальная сумма не должна превышать общую сумму ордера",
    path: ["max_amount"]
}).superRefine((data, context) => {
    if (data.order_type !== 'deposit') return;
    if (!isValidDepositOrderCapacity(data.from_amount)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: DEPOSIT_ORDER_CAPACITY_ERROR, path: ['from_amount'] });
    }

    if (data.from_currency === 'KGS' && !kgsBankId(data.recipient_bank)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'Выберите банк из списка', path: ['recipient_bank'] });
    }
    const types = data.requisite_types || [];
    if (types.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Выберите хотя бы один тип реквизита',
            path: ['requisite_types'],
        });
    }
    if (types.includes('sbp')) {
        const digits = (data.phone_number || '').replace(/\D/g, '');
        if (!((digits.length === 11 && digits.startsWith('7'))
            || (digits.length === 12 && digits.startsWith('996')))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Для СБП укажите корректный номер телефона',
                path: ['phone_number'],
            });
        }
    }
    if (types.includes('card')) {
        const digits = (data.recipient_card || '').replace(/\D/g, '');
        if (digits.length < 13 || digits.length > 19) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Для оплаты картой укажите корректный номер карты',
                path: ['recipient_card'],
            });
        }
    }
    if (types.includes('elqr')) {
        const digits = (data.phone_number || '').replace(/\D/g, '');
        if (data.from_currency !== 'KGS') {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'ElQR доступен только для KGS', path: ['requisite_types'] });
        }
        if (digits.length !== 12 || !digits.startsWith('996')) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'Для ElQR укажите номер телефона в формате +996', path: ['phone_number'] });
        }
        if (!data.recipient_name?.trim()) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'Укажите имя и фамилию получателя', path: ['recipient_name'] });
        }
        if (!data.recipient_bank?.trim()) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'Укажите банк получателя', path: ['recipient_bank'] });
        }
        if (!data.qr_url?.trim().toLowerCase().startsWith('https://')) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'Укажите HTTPS-ссылку для диплинка ElQR', path: ['qr_url'] });
        }
    }
});

type OrderFormData = z.infer<typeof orderSchema>;

interface CreateOrderFormProps {
    onOrderCreated: () => void;
    orderType: 'deposit' | 'withdraw'; // Тип операции передается извне
}

export const CreateOrderForm: React.FC<CreateOrderFormProps> = ({ onOrderCreated, orderType }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [exchangeRate, setExchangeRate] = useState<number | null>(null);
    const [fiatCurrency, setFiatCurrency] = useState<'RUB' | 'KGS' | 'KZT'>('RUB');
    const [loadingRate, setLoadingRate] = useState(true);

    const loadTraderRate = useCallback(async () => {
        try {
            const settings = await traderAPI.getSettings();
            setExchangeRate(settings.exchange_rate ?? null);
            setFiatCurrency(settings.fiat_currency || 'RUB');
        } catch (error) {
            setExchangeRate(null);
            console.error('Ошибка загрузки курса трейдера:', error);
        } finally {
            setLoadingRate(false);
        }
    }, []);

    useEffect(() => {
        loadTraderRate();
        const interval = setInterval(
            loadTraderRate,
            TRADER_BALANCE_REFRESH_INTERVAL_MS,
        );
        return () => clearInterval(interval);
    }, [loadTraderRate]);


    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        reset,
    } = useForm<OrderFormData>({
        resolver: zodResolver(orderSchema),
        defaultValues: {
            order_type: orderType, // Используем переданный тип
            from_currency: 'RUB',
            to_currency: 'USDT',
            duration_minutes: 60, // 1 час по умолчанию
            min_amount: 1000,
            max_amount: 10000,
            requisite_types: [],
        },
    });

    // Устанавливаем тип ордера при монтировании
    useEffect(() => {
        setValue('order_type', orderType);
        if (orderType === 'deposit') {
            setValue('from_currency', fiatCurrency);
            setValue('to_currency', 'USDT');
        } else if (orderType === 'withdraw') {
            setValue('from_currency', fiatCurrency);
            setValue('to_currency', 'USDT'); // Трейдер получает USDT
        }
    }, [orderType, fiatCurrency, setValue]);

    // Вычисляем автоматически количество валюты
    const fromAmount = watch('from_amount');
    const calculatedToAmount = fromAmount && exchangeRate ? fromAmount / exchangeRate : 0;
    const requisiteTypes = watch('requisite_types') || [];

    const toggleRequisiteType = (type: 'sbp' | 'card' | 'elqr', checked: boolean) => {
        const nextTypes = checked
            ? Array.from(new Set([...requisiteTypes, type]))
            : requisiteTypes.filter(item => item !== type);
        setValue('requisite_types', nextTypes, { shouldValidate: true });
    };

    const onSubmit = async (data: OrderFormData) => {
        try {
            setIsLoading(true);
            setError(null);
            setSuccess(null);

            // Для вывода: устанавливаем min_amount и max_amount равными from_amount
            // Для пополнения: используем введенные значения
            const orderData = {
                ...data,
                // Валютная пара определяется профилем трейдера. Не полагаемся
                // на скрытые поля формы: после reset() они могли вернуть RUB.
                from_currency: fiatCurrency,
                to_currency: 'USDT',
                to_amount: calculatedToAmount,
                min_amount: orderType === 'withdraw' ? data.from_amount : (data.min_amount || 0),
                max_amount: orderType === 'withdraw' ? data.from_amount : (data.max_amount || 0),
            };

            console.log('Отправляем данные ордера:', orderData);

            await ordersAPI.createOrder(orderData);

            setSuccess('Ордер успешно создан!');
            reset({
                order_type: orderType,
                from_currency: fiatCurrency,
                to_currency: 'USDT',
                duration_minutes: 60,
                min_amount: 1000,
                max_amount: 10000,
                requisite_types: [],
            });
            onOrderCreated();
            // Уведомляем sidebar об обновлении баланса
            balanceEvents.emit(BALANCE_EVENTS.ORDER_CREATED);
        } catch (err: any) {
            console.error('Ошибка создания ордера:', err);
            setError(
                err.response?.data?.detail ||
                'Произошла ошибка при создании ордера'
            );
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    {orderType === 'deposit' ? 'Cоздать реквизит на пополнение' : 'Создать ордер на вывод'}
                </CardTitle>
                <CardDescription>
                    {orderType === 'deposit'
                        ? `Принимайте ${fiatCurrency} от пользователей и отдавайте им USDT`
                        : `Отдавайте ${fiatCurrency} пользователям и получайте от них USDT`
                    }
                </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit(onSubmit)}>
                <CardContent className="space-y-6">
                    {error && (
                        <div className="p-3 text-sm text-red-500 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="p-3 text-sm text-green-500 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md">
                            {success}
                        </div>
                    )}

                    {/* Скрытое поле типа операции */}
                    <input type="hidden" {...register('order_type')} />

                    {/* Скрытые поля валют */}
                    <input type="hidden" {...register('from_currency')} />
                    <input type="hidden" {...register('to_currency')} />

                    {/* Количества */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="from_amount">
                                {orderType === 'deposit' ? `Общая сумма ордера (${fiatCurrency})` : `Сумма ${fiatCurrency} к выдаче`}
                            </Label>
                            <Input
                                id="from_amount"
                                type="number"
                                step={orderType === 'deposit' ? "1" : "0.01"}
                                min={orderType === 'deposit' ? 1 : 0}
                                max={orderType === 'deposit' ? MAX_DEPOSIT_ORDER_CAPACITY : undefined}
                                placeholder={orderType === 'deposit' ? "10000" : "10000.00"}
                                {...register('from_amount', { valueAsNumber: true })}
                                disabled={isLoading}
                            />
                            {errors.from_amount && (
                                <p className="text-sm text-red-500">{errors.from_amount.message}</p>
                            )}
                            <div className="text-xs text-gray-500 dark:text-slate-400">
                                {orderType === 'deposit'
                                    ? `Сколько ${fiatCurrency} готовы принять от пользователей`
                                    : `Сколько ${fiatCurrency} готовы отдать пользователям`
                                }
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="to_amount">
                                Примерный эквивалент в USDT (без комиссии)
                            </Label>
                            <div className="relative">
                                <Input
                                    id="to_amount"
                                    type="text"
                                    value={loadingRate ? 'Загрузка курса...' : exchangeRate ? formatAmount(calculatedToAmount) : 'Курс недоступен'}
                                    disabled={true}
                                    className="bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-slate-200"
                                />

                            </div>
                            <div className="text-xs text-gray-500 dark:text-slate-400">
                                {loadingRate ? (
                                    'Загружается актуальный курс...'
                                ) : exchangeRate ? (
                                    `Курс: 1 USDT = ${formatAmount(exchangeRate)} ${fiatCurrency}`
                                ) : (
                                    'Оценка USDT недоступна'
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                Курс фиксируется отдельно при назначении каждой заявки.
                            </p>
                        </div>
                    </div>

                    {/* Диапазон принимаемых платежей - только для пополнения */}
                    {orderType === 'deposit' && (
                        <div className="space-y-4 border-t pt-4">
                            <h3 className="text-lg font-medium">Диапазон принимаемых платежей</h3>
                            <p className="text-sm text-gray-600 dark:text-slate-300">
                                Укажите минимальную и максимальную сумму платежей, которые вы готовы принимать от пользователей. Диапазон не должен превышать общую сумму ордера.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="min_amount">Минимальная сумма ({fiatCurrency})</Label>
                                    <Input
                                        id="min_amount"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="1000.00"
                                        {...register('min_amount', { valueAsNumber: true })}
                                        disabled={isLoading}
                                    />
                                    {errors.min_amount && (
                                        <p className="text-sm text-red-500">{errors.min_amount.message}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="max_amount">Максимальная сумма ({fiatCurrency})</Label>
                                    <Input
                                        id="max_amount"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="5000.00"
                                        {...register('max_amount', { valueAsNumber: true })}
                                        disabled={isLoading}
                                    />
                                    {errors.max_amount && (
                                        <p className="text-sm text-red-500">{errors.max_amount.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Для вывода - диапазон не нужен */}
                    {orderType === 'withdraw' && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                                💡 Для вывода диапазон не требуется - вы переводите всю указанную сумму клиентам по их заявкам.
                            </p>
                        </div>
                    )}



                    {/* Реквизиты - только для пополнения */}
                    {orderType === 'deposit' && (
                        <div className="space-y-4 border-t pt-4">
                            <h3 className="text-lg font-medium">Реквизиты</h3>
                            <p className="text-sm text-gray-600 dark:text-slate-300">
                                Укажите свои реквизиты для получения средств от пользователей
                            </p>

                            <div className="space-y-2">
                                <Label>Доступные типы оплаты</Label>
                                <div className="flex flex-wrap gap-4 rounded-md border p-3">
                                    <label className="flex cursor-pointer items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={requisiteTypes.includes('sbp')}
                                            onChange={(event) => toggleRequisiteType('sbp', event.target.checked)}
                                            disabled={isLoading}
                                            className="h-4 w-4 rounded border-gray-300 dark:border-slate-700"
                                        />
                                        <span>СБП</span>
                                    </label>
                                    {fiatCurrency === 'KGS' && (
                                        <label className="flex cursor-pointer items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={requisiteTypes.includes('elqr')}
                                                onChange={(event) => toggleRequisiteType('elqr', event.target.checked)}
                                                disabled={isLoading}
                                                className="h-4 w-4 rounded border-gray-300 dark:border-slate-700"
                                            />
                                            <span>ElQR</span>
                                        </label>
                                    )}
                                    <label className="flex cursor-pointer items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={requisiteTypes.includes('card')}
                                            onChange={(event) => toggleRequisiteType('card', event.target.checked)}
                                            disabled={isLoading}
                                            className="h-4 w-4 rounded border-gray-300 dark:border-slate-700"
                                        />
                                        <span>Банковская карта</span>
                                    </label>
                                </div>
                                {errors.requisite_types && (
                                    <p className="text-sm text-red-500">{errors.requisite_types.message}</p>
                                )}
                            </div>

                            {requisiteTypes.length > 0 && <>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="recipient_name">
                                            {requisiteTypes.includes('elqr') ? 'Фамилия и имя получателя *' : 'Получатель'}
                                        </Label>
                                        <Input
                                            id="recipient_name"
                                            type="text"
                                            placeholder="ФИО получателя"
                                            {...register('recipient_name')}
                                            disabled={isLoading}
                                        />
                                        {errors.recipient_name && (
                                            <p className="text-sm text-red-500">{errors.recipient_name.message}</p>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        {fiatCurrency === 'KGS' ? (
                                            <>
                                                <Label htmlFor="recipient_bank">Банк получателя</Label>
                                                <KgsBankSelect
                                                    id="recipient_bank"
                                                    value={watch('recipient_bank') || ''}
                                                    onChange={value => setValue('recipient_bank', value, { shouldValidate: true })}
                                                    disabled={isLoading}
                                                />
                                                {errors.recipient_bank && (
                                                    <p className="text-sm text-red-500">{errors.recipient_bank.message}</p>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <BankAutocomplete
                                                    id="recipient_bank"
                                                    label="Банк получателя"
                                                    value={watch('recipient_bank') || ''}
                                                    onChange={(value) => setValue('recipient_bank', value)}
                                                    placeholder="Начните вводить название банка..."
                                                    disabled={isLoading}
                                                    error={errors.recipient_bank?.message}
                                                />
                                                {/* Скрытое поле для регистрации в react-hook-form */}
                                                <input type="hidden" {...register('recipient_bank')} />
                                            </>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        {requisiteTypes.includes('sbp') || requisiteTypes.includes('elqr') ? (
                                            <>
                                                <PhoneInput
                                                    id="phone_number"
                                                    label="Номер телефона"
                                                    value={watch('phone_number') || ''}
                                                    onChange={(value) => setValue('phone_number', value)}
                                                    disabled={isLoading}
                                                    error={errors.phone_number?.message}
                                                    country={fiatCurrency === 'KGS' ? 'KG' : 'RU'}
                                                    placeholder={fiatCurrency === 'KGS' ? '+996 (555) 123-456' : '+7 (999) 123-45-67'}
                                                />
                                                <input type="hidden" {...register('phone_number')} />
                                            </>
                                        ) : (
                                            <>
                                                <CardInput
                                                    id="recipient_card"
                                                    label="Номер банковской карты"
                                                    value={watch('recipient_card') || ''}
                                                    onChange={(value) => setValue('recipient_card', value)}
                                                    disabled={isLoading}
                                                    error={errors.recipient_card?.message}
                                                />
                                                <input type="hidden" {...register('recipient_card')} />
                                            </>
                                        )}
                                    </div>

                                    {(requisiteTypes.includes('sbp') || requisiteTypes.includes('elqr')) && requisiteTypes.includes('card') && (
                                        <div className="space-y-2">
                                            <CardInput
                                                id="recipient_card"
                                                label="Номер банковской карты"
                                                value={watch('recipient_card') || ''}
                                                onChange={(value) => setValue('recipient_card', value)}
                                                disabled={isLoading}
                                                error={errors.recipient_card?.message}
                                            />
                                            <input type="hidden" {...register('recipient_card')} />
                                        </div>
                                    )}
                                    {requisiteTypes.includes('elqr') && (
                                        <div className="space-y-2 md:col-span-2">
                                            <Label htmlFor="qr_url">Ссылка для диплинка ElQR *</Label>
                                            <Input
                                                id="qr_url"
                                                type="url"
                                                placeholder="https://example.com/elqr/payment/..."
                                                {...register('qr_url')}
                                                disabled={isLoading}
                                            />
                                            <p className="text-xs text-gray-500 dark:text-slate-400">QR-код будет создан автоматически из этой ссылки.</p>
                                            {errors.qr_url && <p className="text-sm text-red-500">{errors.qr_url.message}</p>}
                                        </div>
                                    )}
                                </div>

                                {/* Примечание */}
                                <div className="space-y-2">
                                    <Label htmlFor="notes">Примечание</Label>
                                    <textarea
                                        id="notes"
                                        placeholder="Дополнительная информация, комментарии..."
                                        {...register('notes')}
                                        disabled={isLoading}
                                        className="w-full p-3 border border-gray-300 dark:border-slate-700 rounded-md resize-none h-20 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    {errors.notes && (
                                        <p className="text-sm text-red-500">{errors.notes.message}</p>
                                    )}
                                </div>
                            </>}
                        </div>
                    )}

                    {/* Для вывода - реквизиты не нужны */}
                    {orderType === 'withdraw' && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                                ℹ️ Для ордеров на вывод реквизиты не требуются. Вы будете переводить средства на реквизиты клиентов, которые они укажут при создании заявки.
                            </p>
                        </div>
                    )}

                    <Button
                        type="submit"
                        className="w-full"
                        disabled={isLoading}
                    >
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Опубликовать реквизит
                    </Button>
                </CardContent>
            </form>
        </Card>
    );
};
